import { FC, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import type { ScoringType, GlobalPresenceBand } from "@/lib/scoring/types";
import { pluralize } from "@/utils/text";
import { CountryInput } from "@/components/filters/Locations/CountryInput";
import { GeoRegionsSelector } from "@/lib/company-filter/components/GeoRegionsSelector";
import { DEFAULT_REGIONS } from "@/lib/company-filter/utils/geoSelection";
import type { RegionId } from "@/lib/company-filter/types";
import { SubsidiaryInput } from "./SubsidiaryInput";

// Array: multinational band + one pre-created empty location band
export const initialBand: GlobalPresenceBand[] = [
  { isMultiNational: false, score: 0 },
  { hqCountryCodes: [], hqRegions: [], score: 0 },
];
function sanitizeLocationBand(b: GlobalPresenceBand): GlobalPresenceBand {
  const sub = b.subsidiaryLocations;
  const hasSubCountries = (sub?.countryCodes?.length ?? 0) > 0;
  const hasSubRegions = (sub?.regions?.length ?? 0) > 0;
  const hasSub = hasSubCountries || hasSubRegions;

  const cleanedSub =
    hasSub && sub
      ? {
          ...(hasSubCountries ? { countryCodes: sub.countryCodes } : {}),
          ...(hasSubRegions ? { regions: sub.regions } : {}),
          ...(sub.countMatching ? { countMatching: sub.countMatching } : {}),
          inclusionMethod: sub.inclusionMethod ?? "ANY_PRESENT",
        }
      : undefined;

  return {
    ...(b.hqCountryCodes?.length ? { hqCountryCodes: b.hqCountryCodes } : {}),
    ...(b.hqRegions?.length ? { hqRegions: b.hqRegions } : {}),
    ...(cleanedSub ? { subsidiaryLocations: cleanedSub } : {}),
    score: b.score,
  };
}

export const sanitize = (c: import("@/lib/scoring/types").ScoringType) => {
  if (!c.globalPresence) return c;
  const [multinational, ...locationBands] =
    c.globalPresence as GlobalPresenceBand[];
  const cleanLocation = locationBands.map(sanitizeLocationBand);
  const bands = multinational?.isMultiNational
    ? [multinational, ...cleanLocation]
    : cleanLocation;
  return { ...c, globalPresence: bands };
};

export const hasErrors = (bands: unknown[]) => {
  const bs = bands as GlobalPresenceBand[];
  if (!bs[0]?.isMultiNational && bs.length <= 1) return true;
  if (bs[0]?.isMultiNational && bs[0]?.score <= 0) return true;
  return bs.slice(1).some((b) => {
    const hasHQ =
      (b.hqCountryCodes?.length ?? 0) > 0 || (b.hqRegions?.length ?? 0) > 0;
    const hasSub =
      (b.subsidiaryLocations?.countryCodes?.length ?? 0) > 0 ||
      (b.subsidiaryLocations?.regions?.length ?? 0) > 0;
    return b.score <= 0 || (!hasHQ && !hasSub);
  });
};
export const getBandSummary = (band: unknown) => {
  const b = band as GlobalPresenceBand;
  if (b.isMultiNational) return "Is multinational";
  const parts: string[] = [];
  if (b.hqCountryCodes?.length)
    parts.push(
      `HQ in ${`${b.hqCountryCodes.length} ${pluralize("country", b.hqCountryCodes.length)}`}`
    );
  if (b.hqRegions?.length) parts.push(`HQ in ${b.hqRegions.join(", ")}`);
  const sub = b.subsidiaryLocations;
  if (sub?.countryCodes?.length || sub?.regions?.length) {
    const names = [
      ...(sub.regions ?? []),
      ...(sub.countryCodes?.length
        ? [
            `${sub.countryCodes.length} ${pluralize("country", sub.countryCodes.length)}`,
          ]
        : []),
    ];
    const method =
      sub.inclusionMethod === "ALL_PRESENT"
        ? "all of"
        : sub.countMatching
          ? `at least ${sub.countMatching} of`
          : "";
    const methodPrefix = method ? `${method} ` : "";
    parts.push(`Has subsidiaries in ${methodPrefix}${names.join(", ")}`);
  }
  return parts.join(", ") || "—";
};

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const GlobalPresenceEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands: GlobalPresenceBand[] = criterion.globalPresence ?? [
    { isMultiNational: false, score: 0 },
  ];
  const [multiNationalBand, ...locationBands] = bands;

  const updateBand = (index: number, patch: Partial<GlobalPresenceBand>) => {
    onChange({
      ...criterion,
      globalPresence: bands.map((b, i) =>
        i === index ? { ...b, ...patch } : b
      ),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      globalPresence: bands.filter((_, i) => i !== index),
    });
  };

  const addLocationBand = () => {
    onChange({
      ...criterion,
      globalPresence: [
        ...bands,
        { hqCountryCodes: [], hqRegions: [], score: 0 },
      ],
    });
  };

  const [showHQ, setShowHQ] = useState<Record<number, boolean>>(() => {
    const init: Record<number, boolean> = {};
    bands.slice(1).forEach((b, i) => {
      init[i + 1] =
        (b.hqCountryCodes?.length ?? 0) > 0 || (b.hqRegions?.length ?? 0) > 0;
    });
    return init;
  });

  // HQ type per band: "country" (default) or "region"
  const [hqType, setHqType] = useState<Record<number, "country" | "region">>(
    () => {
      const init: Record<number, "country" | "region"> = {};
      bands.slice(1).forEach((b, i) => {
        init[i + 1] = (b.hqRegions?.length ?? 0) > 0 ? "region" : "country";
      });
      return init;
    }
  );

  return (
    <Box sx={{ px: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.5 }}
        >
          CRITERIA
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.5 }}
        >
          POINTS
        </Typography>
      </Box>

      {/* Band 0: Multinational — 2-column layout matching location bands */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={multiNationalBand.isMultiNational ?? false}
                onChange={({ target: { checked } }) => {
                  updateBand(0, {
                    isMultiNational: checked,
                    score: checked ? multiNationalBand.score : 0,
                  });
                }}
              />
            }
            label="Score for companies that are multinational"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            pt: 1,
          }}
        >
          <TextField
            size="small"
            type="number"
            label="Pts"
            disabled={!multiNationalBand.isMultiNational}
            error={
              multiNationalBand.isMultiNational && multiNationalBand.score === 0
            }
            value={multiNationalBand.score === 0 ? "" : multiNationalBand.score}
            onChange={(e) =>
              updateBand(0, {
                score: e.target.value === "" ? 0 : Number(e.target.value),
              })
            }
            inputProps={{ min: 0, step: 1 }}
            sx={{ width: 72 }}
          />
        </Box>
      </Box>

      {/* Location bands */}
      {locationBands.map((band, i) => {
        const bandIndex = i + 1;
        const hqExpanded = showHQ[bandIndex] ?? false;
        const currentHqType = hqType[bandIndex] ?? "country";
        // hasSub: show SubInput as soon as user clicked ADD SUBSIDIARY (even with empty content)
        const hasSub = band.subsidiaryLocations !== undefined;
        const hasHQContent =
          (band.hqCountryCodes?.length ?? 0) > 0 ||
          (band.hqRegions?.length ?? 0) > 0;
        const hasSubContent =
          (band.subsidiaryLocations?.countryCodes?.length ?? 0) > 0 ||
          (band.subsidiaryLocations?.regions?.length ?? 0) > 0;
        const userInteracted = hqExpanded || hasSub;
        // Error only when user has opened HQ/sub but neither has any selection
        const hasLocationError =
          userInteracted && !hasHQContent && !hasSubContent;
        const showScoreError = band.score === 0 && userInteracted;

        return (
          <Box
            key={bandIndex}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 1,
              mb: 1,
            }}
          >
            {/* Left: criteria card */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {/* HQ section */}
                {hqExpanded ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Headquarters by
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          const next =
                            currentHqType === "country" ? "region" : "country";
                          setHqType((prev) => ({ ...prev, [bandIndex]: next }));
                          updateBand(bandIndex, {
                            hqCountryCodes: [],
                            hqRegions: [],
                          });
                        }}
                        sx={{
                          color: "primary.main",
                          fontWeight: 700,
                          fontSize: 13,
                          p: 0,
                          minWidth: 0,
                          textTransform: "none",
                        }}
                      >
                        {currentHqType === "country" ? "Country ▾" : "Region ▾"}
                      </Button>
                    </Box>
                    {currentHqType === "country" ? (
                      <CountryInput
                        selectedItems={band.hqCountryCodes ?? []}
                        onCountrySelect={(alpha2s) =>
                          updateBand(bandIndex, { hqCountryCodes: alpha2s })
                        }
                        isMultiple
                        placeholderText="Headquarters Locations"
                      />
                    ) : (
                      <GeoRegionsSelector
                        regions={DEFAULT_REGIONS}
                        selectedRegionIds={
                          (band.hqRegions ?? []).filter((r) =>
                            DEFAULT_REGIONS.some((dr) => dr.id === r)
                          ) as RegionId[]
                        }
                        onToggle={(regionId, checked) =>
                          updateBand(bandIndex, {
                            hqRegions: checked
                              ? [...(band.hqRegions ?? []), regionId]
                              : (band.hqRegions ?? []).filter(
                                  (r) => r !== regionId
                                ),
                          })
                        }
                      />
                    )}
                  </Box>
                ) : (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setShowHQ((prev) => ({ ...prev, [bandIndex]: true }))
                    }
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: 0.5,
                      alignSelf: "flex-start",
                    }}
                  >
                    ADD HEADQUARTER LOCATIONS
                  </Button>
                )}

                {/* Subsidiary section */}
                {hasSub ? (
                  <SubsidiaryInput
                    band={band}
                    onUpdate={(patch) => updateBand(bandIndex, patch)}
                    onClear={() =>
                      updateBand(bandIndex, { subsidiaryLocations: undefined })
                    }
                  />
                ) : (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateBand(bandIndex, {
                        subsidiaryLocations: {
                          countryCodes: [],
                          regions: [],
                          inclusionMethod: "ANY_PRESENT",
                        },
                      })
                    }
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: 0.5,
                      alignSelf: "flex-start",
                    }}
                  >
                    ADD SUBSIDIARY LOCATIONS
                  </Button>
                )}

                {hasLocationError && (
                  <Typography variant="caption" color="error">
                    You must select at least one country or region
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right: points + delete */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                pt: 1,
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Pts"
                error={showScoreError}
                value={band.score === 0 ? "" : band.score}
                onChange={(e) =>
                  updateBand(bandIndex, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 72 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(bandIndex)}
                aria-label="Remove location band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addLocationBand}
        sx={{ color: "primary.main", fontWeight: 600 }}
      >
        ADD LOCATION BAND
      </Button>
    </Box>
  );
};
